import React from 'react';
import { Chip, CircularProgress, Grid, IconButton, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { OpenInNew } from '@material-ui/icons';
import { AlertTriangle } from 'lucide-react';

import { useGetElogiiTaskSummaryQuery } from 'shared/hooks/system/useGetElogiiTaskSummaryQuery';
import dayjs from 'utils/dayjsConfig';

const EMPTY_VALUE = '—';

const STATUS_CLASS_BY_NAME = {
    COMPLETED: 'statusSuccess',
    PICKUP_FAILED: 'statusFailed',
    FAILED: 'statusFailed',
    CANCELLED: 'statusFailed',
    'NOT FOUND': 'statusFailed',
};

function getStatusClassKey(stateName) {
    return STATUS_CLASS_BY_NAME[stateName?.toUpperCase()] ?? 'statusInProgress';
}

function shipmentHasTaskUrl(shipment) {
    return Boolean(shipment?.routeManagerTaskUrl);
}

function getRouteProviderExternalId(shipments) {
    return shipments?.find((shipment) => shipment?.routeProviderExternalId)?.routeProviderExternalId || null;
}

function shouldShowElogiiPanel(carrier, shipments) {
    if (!shipments?.length) {
        return false;
    }
    const hasRoutingFields = shipments.some(
        (shipment) => shipment?.routingStatus != null && shipment?.routeProvider != null,
    );
    if (!hasRoutingFields) {
        return false;
    }
    if (carrier?.toLowerCase() === 'dbi') {
        return true;
    }
    return shipments.some(shipmentHasTaskUrl);
}

function shouldFetchElogiiTask(carrier, shipments) {
    if (!shouldShowElogiiPanel(carrier, shipments)) {
        return false;
    }
    return shipments.some(shipmentHasTaskUrl);
}

function getRoutingStatusChipClass(routingStatus) {
    const normalized = routingStatus?.toUpperCase();
    if (normalized === 'COMPLETED') {
        return 'statusSuccess';
    }
    if (normalized === 'CANCELLED') {
        return 'statusFailed';
    }
    if (normalized === 'PENDING') {
        return 'statusPending';
    }
    return 'statusInProgress';
}

const useStyles = makeStyles((theme) => ({
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    summaryPanel: {
        backgroundColor: 'inherit',
        borderRadius: 4,
        padding: 0,
        overflow: 'hidden',
        boxShadow: '0 2px 7px rgba(0, 0, 0, 0.14)',
    },
    summaryContent: {
        padding: theme.spacing(2.5, 3),
    },
    field: {
        textAlign: 'center',
    },
    fieldLabel: {
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    fieldValue: {
        fontWeight: 500,
    },
    taskIdValue: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
    },
    taskLinkButton: {
        color: theme.palette.text.secondary,
    },
    shipmentSummary: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: theme.spacing(1, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    shipmentSummaryLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 600,
        marginRight: theme.spacing(1),
    },
    shipmentChips: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: theme.spacing(-0.5),
        '& > *': {
            margin: theme.spacing(0.5),
        },
    },
    statusChip: {
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 500,
    },
    statusSuccess: {
        backgroundColor: '#43a047',
        color: '#fff',
    },
    statusFailed: {
        backgroundColor: '#9e9e9e',
        color: '#fff',
    },
    statusPending: {
        backgroundColor: '#f9a825',
        color: '#fff',
    },
    statusInProgress: {
        backgroundColor: '#1976d2',
        color: '#fff',
    },
    feedbackText: {
        color: theme.palette.text.secondary,
        textAlign: 'center',
    },
    feedbackContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    warningIcon: {
        color: theme.palette.error.main,
    },
}));

function formatTimestamp(value) {
    if (!value) {
        return EMPTY_VALUE;
    }
    return dayjs(value).local().format('MM/DD/YYYY h:mm a');
}

function getStatusClassName(classes, stateName) {
    return `${classes.statusChip} ${classes[getStatusClassKey(stateName)]}`;
}

function getRoutingChipClassName(classes, routingStatus) {
    return `${classes.statusChip} ${classes[getRoutingStatusChipClass(routingStatus)]}`;
}

function ElogiiTaskSummary({ carrier, shipments = [] }) {
    const classes = useStyles();
    const shouldShow = shouldShowElogiiPanel(carrier, shipments);
    const shouldFetch = shouldFetchElogiiTask(carrier, shipments);
    const taskId = getRouteProviderExternalId(shipments);
    const { data, error, isError, isLoading } = useGetElogiiTaskSummaryQuery(taskId, { enabled: shouldFetch });
    const task = data?.task;

    const renderShipmentChips = () =>
        shipments.length ? (
            <div className={classes.shipmentSummary}>
                <Typography className={classes.shipmentSummaryLabel}>Route Request Status:</Typography>
                <div className={classes.shipmentChips}>
                    {shipments.map((shipment) => (
                        <Chip
                            key={shipment.id}
                            size="small"
                            label={`${shipment.id} · ${shipment.routingStatus || 'Unknown'}`}
                            className={getRoutingChipClassName(classes, shipment.routingStatus)}
                        />
                    ))}
                </div>
            </div>
        ) : null;

    const renderPanel = (content) => (
        <section className={classes.section}>
            <Paper elevation={0} className={classes.summaryPanel}>
                {renderShipmentChips()}
                <div className={classes.summaryContent}>{content}</div>
            </Paper>
        </section>
    );

    const renderTaskFields = (taskFields) => (
        <Grid container spacing={2}>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>Task ID</Typography>
                <div className={classes.taskIdValue}>
                    <Typography variant="body2" className={classes.fieldValue}>
                        {taskFields.taskId || EMPTY_VALUE}
                    </Typography>
                    {taskFields.url ? (
                        <IconButton
                            component="a"
                            href={taskFields.url}
                            target="_blank"
                            rel="noreferrer"
                            size="small"
                            aria-label="Open eLogii task"
                            className={classes.taskLinkButton}
                        >
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    ) : null}
                </div>
            </Grid>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>Assigned Driver ID</Typography>
                <Typography variant="body2" className={classes.fieldValue}>
                    {taskFields.driverId || EMPTY_VALUE}
                </Typography>
            </Grid>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>Status</Typography>
                <Chip
                    size="small"
                    label={taskFields.stateName || 'Unknown'}
                    className={getStatusClassName(classes, taskFields.stateName)}
                />
            </Grid>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>En Route at</Typography>
                <Typography variant="body2">{formatTimestamp(taskFields.enRouteTime)}</Typography>
            </Grid>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>Arrived at</Typography>
                <Typography variant="body2">{formatTimestamp(taskFields.actualArrivalTime)}</Typography>
            </Grid>
            <Grid item xs={6} sm={2} className={classes.field}>
                <Typography className={classes.fieldLabel}>Completed at</Typography>
                <Typography variant="body2">{formatTimestamp(taskFields.completedTime)}</Typography>
            </Grid>
        </Grid>
    );

    if (!shouldShow) {
        return null;
    }

    if (!shouldFetch) {
        return renderPanel(
            <>
                <h2>eLogii Summary</h2>
                <br />
                <Typography variant="body2" className={classes.feedbackText}>
                    Waiting on eLogii...
                </Typography>
            </>,
        );
    }

    if (isLoading) {
        return renderPanel(<CircularProgress size={26} />);
    }

    if (isError) {
        if (error?.statusCode === 404) {
            return renderPanel(
                <>
                    <h2>eLogii Summary</h2>
                    <br />
                    {renderTaskFields({
                        actualArrivalTime: null,
                        completedTime: null,
                        driverId: null,
                        enRouteTime: null,
                        stateName: 'Not Found',
                        taskId: getRouteProviderExternalId(shipments),
                        url: null,
                    })}
                </>,
            );
        }

        return renderPanel(
            <div className={classes.feedbackContent}>
                <AlertTriangle className={`h-6 w-6 ${classes.warningIcon}`} role="img" aria-label="Warning" />
                <Typography variant="body2" color="error" className={classes.feedbackText}>
                    Failed to load eLogii task information.
                </Typography>
            </div>,
        );
    }

    if (!task) {
        return renderPanel(
            <>
                <h2>eLogii Summary</h2>
                <br />
            </>,
        );
    }

    return renderPanel(
        <>
            <h2>eLogii Summary</h2>
            <br />
            {renderTaskFields(task)}
        </>,
    );
}

export { ElogiiTaskSummary };
